import { useEffect, useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { debounce } from '../../utils/debounce';
import { IQueryFilteredParams } from './types';

type Keyable =
  | string
  | number
  | boolean
  | null
  | undefined
  | Keyable[]
  | { [k: string]: Keyable };

function normalizeForKey(input: unknown): Keyable {
  if (input instanceof Date) return input.toISOString();
  if (Array.isArray(input)) return input.map(normalizeForKey);
  if (input && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input as Record<string, unknown>)
        .filter(([, v]) => v !== undefined) // drop undefineds
        .map(([k, v]) => [k, normalizeForKey(v)]),
    );
  }
  return input as Keyable;
}

export const useQueryFilteredPagination = <
  TData,
  TResponse,
  TFilters extends Record<string, any> = {},
>({
  queryFn,
  queryKey,
  serialize,
  limit = 50,
  enabled = true,
  initialFilters,
}: IQueryFilteredParams<TData, TResponse, TFilters>) => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [debounceSearch, setDebounceSearch] = useState('');
  const [filters, setFilters] = useState<Partial<TFilters>>(
    initialFilters ?? {},
  );

  const normalizedFilters = useMemo(
    () => normalizeForKey(filters) as Partial<TFilters>,
    [filters],
  );

  const {
    data,
    isLoading: isLoadingQuery,
    isRefetching,
    refetch,
    fetchNextPage: next,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    enabled,
    queryKey: [
      ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
      debounceSearch,
      limit,
      normalizedFilters,
    ],
    queryFn: async ({ pageParam }) =>
      await queryFn({
        search: debounceSearch,
        limit,
        skip: pageParam,
        ...(filters as TFilters),
      }),
    initialPageParam: 0,
    getNextPageParam: ({ cursor, count }) =>
      cursor < count ? cursor : undefined,
    select: (data) => serialize(data.pages.flatMap((page) => page.nodes)),
  });

  useEffect(() => {
    setIsLoading(isLoadingQuery);
  }, [isLoadingQuery]);

  const debounceSearchHandler = debounce((value: string) => {
    setIsLoading(false);
    setDebounceSearch(value);
  }, 350);

  const updateFilters = (patch: Partial<TFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const onSearch = (value: string) => {
    setIsLoading(true);
    setSearch(value);
    debounceSearchHandler(value);
  };

  const reset = () => {
    setSearch('');
    setIsLoading(false);
    setDebounceSearch('');
    setFilters(initialFilters ?? {});
  };

  return {
    reset,
    data: data ?? [],
    onSearch,
    search,
    isLoading: isLoading || isLoadingQuery,
    next,
    hasNext: hasNextPage,
    isFetchingNextPage,
    filters,
    setFilters: updateFilters,
  };
};
